import * as Sentry from '@sentry/react';
import type { Query } from '@tanstack/react-query';
import { type GetPdpQuery, useGetPdpQuery } from '@vizit/api';
import type { APIError } from '@vizit/api';
import { PdpScoreStatus, PdpStatus, type PdpView } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';

/**
 * Props for the usePdpData hook
 */
interface UsePdpDataProps {
  /**
   * The organization ID
   */
  organizationId: string;
  /**
   * The portfolio ID
   */
  workspaceId: string;
  /**
   * The PDP ID
   */
  pdpId: string;
  /**
   * The function to call when authentication errors occur
   */
  onAuthenticationError?: (error: unknown | APIError) => void;
}

const SETTLED_SCORE_STATUSES = new Set<string>([PdpScoreStatus.Scored, PdpScoreStatus.Failed]);

function isScoreSettled(scoreStatus: string | null | undefined): boolean {
  return !!scoreStatus && SETTLED_SCORE_STATUSES.has(scoreStatus);
}

/**
 * Return type for the usePdpData hook
 */
interface UsePdpDataReturn {
  /**
   * The PDP data
   */
  data: PdpView | undefined;
  /**
   * Whether the PDP data is loading
   */
  isLoading: boolean;
  /**
   * Whether polling is active (score_status is not 'SCORED')
   */
  isPolling: boolean;
  /**
   * Any error that occurred
   */
  error: APIError | null;
}

/**
 * Hook to fetch PDP data with polling functionality
 *
 * Polls every 5 seconds if score_status is not 'SCORED', stops after 5 minutes or when scored
 *
 * @param props - The hook configuration
 * @returns The PDP data and loading states
 */
export const usePdpData = (props: UsePdpDataProps): UsePdpDataReturn => {
  const { organizationId, workspaceId, pdpId, onAuthenticationError } = props;

  const { data, isLoading, error } = useGetPdpQuery(
    {
      organization_id: organizationId ?? '',
      workspace_id: workspaceId ?? '',
      pdp_id: pdpId,
    },
    {
      enabled: !!(organizationId && workspaceId && pdpId),
      retry: false,
      refetchInterval: (query: Query<GetPdpQuery, APIError>) => {
        if (query.state.error && (query.state.error as APIError).status === 404) {
          return false;
        }

        // Poll for 5 minutes maximum (60 polls at 5-second intervals)
        const maxPolls = 60; // score-threshold-allow: poll count, not a score band
        const maxRetriesReached = query.state.dataUpdateCount >= maxPolls;

        // If score_status is not 'SCORED' and we haven't exceeded max polls, poll every 5 seconds.
        // FAILED is terminal until the user refreshes, so it counts as settled.
        const isScored = isScoreSettled(query.state.data?.score_status);
        const isScraping = query.state.data?.status === PdpStatus.ScrapeStarted;
        if ((!isScored || isScraping) && !maxRetriesReached) {
          return 5000; // 5 seconds
        }

        if ((!isScored || isScraping) && maxRetriesReached) {
          Sentry.withScope((scope) => {
            scope.setFingerprint([
              useGetPdpQuery.schema.method.toUpperCase(),
              useGetPdpQuery.schema.path,
              'polling_timeout',
            ]);

            Sentry.captureException(new Error('Max retries reached for pdp data query.'), {
              level: 'warning',
              tags: {
                feature: SENTRY_FEATURE_TAGS.Product,
                stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productDataPolling,
              },
              extra: {
                organization_id: organizationId,
                workspace_id: workspaceId,
                pdp_id: pdpId,
                retry_count: query.state.dataUpdateCount,
                max_count: maxPolls,
              },
            });
          });
        }

        // Stop polling if score is 'SCORED' or max polls reached
        return false;
      },
      refetchIntervalInBackground: true,
    },
  );

  if (error) {
    onAuthenticationError?.(error);
  }

  const isPolling = !error && (!isScoreSettled(data?.score_status) || data?.status === PdpStatus.ScrapeStarted);

  return {
    data,
    isLoading,
    isPolling,
    error: error || null,
  };
};
